import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import {
  buildingGeomAttributeTagNew,
  COLUMN,
  INDEX_ATTRIBUTE,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { PorchKeyMeasuresGeometryType } from "building-types/porches/porchKeyMeasuresTypes";
import type { RWTable, Table } from "common/data/table";
import { addAnalyticExtrusion } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";

export function addPorchAnalyticEntities(
  building: ReturnType<typeof select>,
  buildingEntityHandle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};
  if (buildingEntityHandle.entityType !== "PORCH") {
    return entities;
  }
  const porch = building.porches[buildingEntityHandle.id];
  if (!porch) {
    return entities;
  }
  const generatedHandle: DocEntityHandle = { type: "GENERATED_ENTITY", entity: buildingEntityHandle };
  for (let geometryIndex = 0; geometryIndex < porch.group.geom.length; geometryIndex += 1) {
    const extrusion = porch.group.geom[geometryIndex];
    const tag = porch.group.tags[geometryIndex];
    if (
      extrusion?.type !== "EXTRUSION" ||
      tag?.porchKeyMeasuresGeometryType !== PorchKeyMeasuresGeometryType.COLUMN_STRUCTURE ||
      tag.columnIndex === undefined
    ) {
      continue;
    }
    addAnalyticExtrusion("ANALYTIC_PORCH", entities, porch.keyMeasures.name, [generatedHandle], extrusion, undefined, [
      COLUMN,
      buildingGeomAttributeTagNew(INDEX_ATTRIBUTE, tag.columnIndex, "NUMBER"),
    ]);
  }
  return entities;
}

const select = makeBuildingSelector(["porches"]);

export function generate(building: ReturnType<typeof select>): Pick<Building, "analyticPorchEntities"> {
  const analyticPorchEntities: RWTable<BuildingAnalyticEntity> = {};
  for (const id of Object.keys(building.porches)) {
    Object.assign(analyticPorchEntities, addPorchAnalyticEntities(building, { entityType: "PORCH", id }));
  }
  return { analyticPorchEntities };
}

export default memo(select, generate);
